/**
 * Respawn13 — Header behaviors
 *   - Mega menu (hover-intent on desktop, click on touch/keyboard)
 *   - Mobile drawer (slide-in, focus trap, Esc to close)
 *   - Predictive search (debounced, abortable, /search/suggest.json)
 */
(function () {
  'use strict';

  var R = window.Respawn = window.Respawn || {};
  var INTENT_DELAY = 150;
  var SEARCH_DEBOUNCE = 220;
  var SEARCH_MIN_CHARS = 2;

  /* -----------------------------------------------------------------------
   * Mega menu
   * --------------------------------------------------------------------- */
  function initMegaMenus() {
    var triggers = document.querySelectorAll('[data-mega-trigger]');
    if (!triggers.length) return;

    var hasHover = window.matchMedia('(hover: hover)').matches;
    var openTimer, closeTimer;

    triggers.forEach(function (trigger) {
      var btn = trigger.querySelector('[data-mega-button]');
      var panel = trigger.querySelector('[data-mega-panel]');
      if (!btn || !panel) return;

      function open() {
        clearTimeout(closeTimer);
        panel.hidden = false;
        btn.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-open');
      }
      function close() {
        panel.hidden = true;
        btn.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('is-open');
      }

      if (hasHover) {
        trigger.addEventListener('mouseenter', function () {
          clearTimeout(closeTimer);
          openTimer = setTimeout(open, INTENT_DELAY);
        });
        trigger.addEventListener('mouseleave', function () {
          clearTimeout(openTimer);
          closeTimer = setTimeout(close, INTENT_DELAY);
        });
        /* Click on the link itself navigates immediately — do NOT preventDefault.
           The menu was opened on hover; clicking the parent link goes to /collections. */
      } else {
        /* Touch / no-hover: tap once to open the panel without navigating.
           The Catalog link itself is reached via the mobile drawer instead. */
        btn.addEventListener('click', function (e) {
          if (!trigger.classList.contains('is-open')) {
            e.preventDefault();
            open();
          }
          /* If already open, second tap navigates (default behavior). */
        });
      }

      btn.addEventListener('keydown', function (e) {
        /* ArrowDown opens the menu and focuses the first item.
           Enter / Space follow the link normally (don't intercept). */
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          open();
          var firstLink = panel.querySelector('a');
          if (firstLink) firstLink.focus();
        }
      });

      panel.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          close();
          btn.focus();
        }
      });

      // Close on click outside
      document.addEventListener('click', function (e) {
        if (!trigger.contains(e.target)) close();
      });
    });
  }

  /* -----------------------------------------------------------------------
   * Mobile drawer
   * --------------------------------------------------------------------- */
  function initMobileDrawer() {
    var drawer = document.querySelector('[data-mobile-drawer]');
    var toggles = document.querySelectorAll('[data-mobile-drawer-toggle]');
    var closes = document.querySelectorAll('[data-mobile-drawer-close]');
    var overlay = document.querySelector('[data-mobile-drawer-overlay]');
    if (!drawer || !toggles.length) return;

    var releaseTrap = null;
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.hidden = false;
      drawer.setAttribute('aria-hidden', 'false');
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      releaseTrap = R.focusTrap(drawer);
      var firstFocusable = drawer.querySelector('a, button');
      if (firstFocusable) firstFocusable.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      toggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      // Allow CSS transition to finish before hiding
      setTimeout(function () { drawer.hidden = true; }, 250);
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    toggles.forEach(function (t) { t.addEventListener('click', open); });
    closes.forEach(function (c) { c.addEventListener('click', close); });
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* -----------------------------------------------------------------------
   * Predictive search
   * --------------------------------------------------------------------- */
  function initPredictiveSearch() {
    var panel = document.querySelector('[data-predictive-search]');
    var input = document.querySelector('[data-predictive-search-input]');
    var results = document.querySelector('[data-predictive-search-results]');
    var openers = document.querySelectorAll('[data-search-toggle]');
    var closers = document.querySelectorAll('[data-search-close]');
    if (!panel || !input || !results) return;

    var controller = null;
    var lastFocus = null;

    function openPanel() {
      lastFocus = document.activeElement;
      panel.hidden = false;
      panel.classList.add('is-open');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'true'); });
      requestAnimationFrame(function () { input.focus(); });
    }

    function closePanel() {
      panel.classList.remove('is-open');
      openers.forEach(function (o) { o.setAttribute('aria-expanded', 'false'); });
      setTimeout(function () { panel.hidden = true; }, 200);
      if (controller) controller.abort();
      results.innerHTML = '';
      input.value = '';
      input.setAttribute('aria-expanded', 'false');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    openers.forEach(function (o) { o.addEventListener('click', openPanel); });
    closers.forEach(function (c) { c.addEventListener('click', closePanel); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) closePanel();
    });

    function renderResults(payload) {
      var items = (payload.resources && payload.resources.results && payload.resources.results.products) || [];
      if (!items.length) {
        results.innerHTML = '<p class="predictive-search__empty">' + (R.t && R.t('search.no_results_html') || 'No results') + '</p>';
        input.setAttribute('aria-expanded', 'true');
        return;
      }
      var html = '<ul class="predictive-search__list" role="listbox">';
      items.forEach(function (p, i) {
        var img = p.featured_image && p.featured_image.url
          ? '<img class="predictive-search__thumb" loading="lazy" alt="" src="' + p.featured_image.url + '&width=80">'
          : '<span class="predictive-search__thumb predictive-search__thumb--placeholder"></span>';
        html += '<li role="option" tabindex="-1">' +
          '<a class="predictive-search__item" href="' + p.url + '">' +
            img +
            '<span class="predictive-search__title">' + escapeHtml(p.title) + '</span>' +
            '<span class="predictive-search__price">' + (p.price || '') + '</span>' +
          '</a>' +
        '</li>';
      });
      html += '</ul>';
      html += '<a class="predictive-search__view-all" href="/search?q=' + encodeURIComponent(input.value) + '">View all results</a>';
      results.innerHTML = html;
      input.setAttribute('aria-expanded', 'true');
    }

    function escapeHtml(s) {
      return String(s).replace(/[&<>"']/g, function (c) {
        return ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c];
      });
    }

    var run = R.debounce(function () {
      var q = input.value.trim();
      if (q.length < SEARCH_MIN_CHARS) {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      if (controller) controller.abort();
      controller = new AbortController();

      var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal, headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(renderResults)
        .catch(function (err) {
          if (err.name !== 'AbortError') {
            results.innerHTML = '<p class="predictive-search__empty">Error loading results.</p>';
          }
        });
    }, SEARCH_DEBOUNCE);

    input.addEventListener('input', run);
  }

  /* -----------------------------------------------------------------------
   * Boot
   * --------------------------------------------------------------------- */
  function boot() {
    initMegaMenus();
    initMobileDrawer();
    initPredictiveSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
